import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const DEFAULT_ZONE = 'us-central1-a';
const DEFAULT_MACHINE_TYPE = 'e2-small';

const DeploymentForm = ({ onDeploymentSuccess, initialAppType, availableApps }) => {
  const [formData, setFormData] = useState({
    name: '',
    appType: initialAppType || '',
    region: 'us-central1',
    zone: DEFAULT_ZONE,
    machineType: DEFAULT_MACHINE_TYPE,
    apiKeys: {
      gemini_api_key: '',
      anthropic_api_key: '',
      openai_api_key: ''
    },
    customInstructions: '',
    webhookUrls: {
      notifications: '',
      completion: ''
    },
    environment: 'production'
  });

  // Set the initialAppType when provided
  useEffect(() => {
    if (initialAppType) {
      setFormData(prevData => ({
        ...prevData,
        appType: initialAppType,
        name: `${initialAppType}-${Math.floor(Math.random() * 10000)}` // Generate a unique name
      }));
      
      // Scroll to the form to draw attention
      setTimeout(() => {
        window.scrollTo({
          top: document.getElementById('deploy-form').offsetTop - 100,
          behavior: 'smooth'
        });
      }, 300);
    }
  }, [initialAppType]);

  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentResult, setDeploymentResult] = useState(null);
  const [apiKeyFields, setApiKeyFields] = useState(['gemini_api_key']);
  const [webhookFields, setWebhookFields] = useState([]);

  // Available app types
  const aiApps = [
    {
      id: 'ai-toybox',
      name: 'AI Toybox',
      description: 'Explore our suite of AI tools for content and analysis'
    },
    {
      id: 'sql-writer',
      name: 'SQL Writer',
      description: 'Generate optimized SQL queries with AI assistance'
    },
    {
      id: 'pyspark-writer',
      name: 'PySpark Writer',
      description: 'Create efficient PySpark transformations and analytics code'
    },
    {
      id: 'creative-writer',
      name: 'Content Studio',
      description: 'Transform ideas into polished professional communications'
    },
    {
      id: 'lesson-plan-creator',
      name: 'Lesson Plan Creator',
      description: 'Creates ready-to-use lesson plans for teachers'
    },
    {
      id: 'course-outline-creator',
      name: 'Course Outline Creator',
      description: 'Creates organized learning plans with clear goals'
    },
    {
      id: 'social-media-manager',
      name: 'Social Media Manager',
      description: 'Create and manage social media content with AI'
    },
    {
      id: 'powerpoint-generator',
      name: 'Slide Generator',
      description: 'Generate professional presentations with AI'
    },
    {
      id: 'grant-finder',
      name: 'Grant Finder',
      description: 'Discover grants tailored to your organization'
    },
    {
      id: 'grant-writer',
      name: 'Grant Writer',
      description: 'Get AI help writing compelling grant proposals'
    },
    {
      id: 'life-coach',
      name: 'Pro Coach',
      description: 'Accelerate career growth with AI guidance'
    },
    {
      id: 'data-dictionary-generator',
      name: 'Data Dictionary Generator',
      description: 'Generate comprehensive data dictionaries'
    },
    {
      id: 'training-manual-creator',
      name: 'Training Manual Creator',
      description: 'Create comprehensive training manuals'
    },
    {
      id: 'research-assistant',
      name: 'Research Assistant',
      description: 'Generate comprehensive research overviews'
    },
    {
      id: 'play-formatter',
      name: 'Play Formatter',
      description: 'Transform text into formatted play scripts'
    }
  ];

  // GCP regions and zones
  const gcpRegions = [
    { id: 'us-central1', name: 'Iowa (us-central1)' },
    { id: 'us-east1', name: 'South Carolina (us-east1)' },
    { id: 'us-west1', name: 'Oregon (us-west1)' },
    { id: 'europe-west1', name: 'Belgium (europe-west1)' },
    { id: 'asia-east1', name: 'Taiwan (asia-east1)' }
  ];

  // GCP machine types
  const machineTypes = [
    { id: 'e2-micro', name: 'E2 Micro (2 vCPU, 1 GB)', price: '$0.008/hour' },
    { id: 'e2-small', name: 'E2 Small (2 vCPU, 2 GB)', price: '$0.017/hour' },
    { id: 'e2-medium', name: 'E2 Medium (2 vCPU, 4 GB)', price: '$0.034/hour' },
    { id: 'n1-standard-1', name: 'N1 Standard (1 vCPU, 3.75 GB)', price: '$0.048/hour' },
    { id: 'n1-standard-2', name: 'N1 Standard (2 vCPU, 7.5 GB)', price: '$0.096/hour' },
    { id: 'n1-standard-4', name: 'N1 Standard (4 vCPU, 15 GB)', price: '$0.192/hour' },
    { id: 'n1-highmem-2', name: 'N1 High Memory (2 vCPU, 13 GB)', price: '$0.144/hour' },
    { id: 'n1-highcpu-2', name: 'N1 High CPU (2 vCPU, 1.8 GB)', price: '$0.072/hour' }
  ];

  // API Key options
  const apiKeyOptions = [
    { id: 'gemini_api_key', name: 'Google Gemini API Key' },
    { id: 'anthropic_api_key', name: 'Anthropic API Key' },
    { id: 'openai_api_key', name: 'OpenAI API Key' },
    { id: 'tavily_api_key', name: 'Tavily API Key' }
  ];

  // Webhook options
  const webhookOptions = [
    { id: 'notifications', name: 'Notifications Webhook' },
    { id: 'completion', name: 'Task Completion Webhook' },
    { id: 'error', name: 'Error Reporting Webhook' }
  ];

  const environments = [
    { id: 'development', name: 'Development' },
    { id: 'staging', name: 'Staging' },
    { id: 'production', name: 'Production' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested objects (apiKeys, webhookUrls)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const addApiKeyField = (e) => {
    e.preventDefault();
    const availableKeys = apiKeyOptions.map(opt => opt.id);
    const unusedKeys = availableKeys.filter(key => !apiKeyFields.includes(key));
    
    if (unusedKeys.length > 0) {
      setApiKeyFields([...apiKeyFields, unusedKeys[0]]);
    }
  };

  const removeApiKeyField = (keyToRemove) => {
    setApiKeyFields(apiKeyFields.filter(key => key !== keyToRemove));
    setFormData({
      ...formData,
      apiKeys: {
        ...formData.apiKeys,
        [keyToRemove]: ''
      }
    });
  };

  const addWebhookField = (e) => {
    e.preventDefault();
    const availableWebhooks = webhookOptions.map(opt => opt.id);
    const unusedWebhooks = availableWebhooks.filter(hook => !webhookFields.includes(hook));
    
    if (unusedWebhooks.length > 0) {
      setWebhookFields([...webhookFields, unusedWebhooks[0]]);
    }
  };

  const removeWebhookField = (hookToRemove) => {
    setWebhookFields(webhookFields.filter(hook => hook !== hookToRemove));
    setFormData({
      ...formData,
      webhookUrls: {
        ...formData.webhookUrls,
        [hookToRemove]: ''
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsDeploying(true);
      setDeploymentResult(null);
      
      // Filter out empty API keys and webhook URLs
      const filteredApiKeys = {};
      for (const [key, value] of Object.entries(formData.apiKeys)) {
        if (value.trim()) {
          filteredApiKeys[key] = value;
        }
      }
      
      const filteredWebhookUrls = {};
      for (const [key, value] of Object.entries(formData.webhookUrls)) {
        if (value.trim()) {
          filteredWebhookUrls[key] = value;
        }
      }
      
      // Prepare request payload
      const payload = {
        ...formData,
        apiKeys: filteredApiKeys,
        webhookUrls: filteredWebhookUrls,
        environment: formData.environment
      };
      
      // Make API request to deploy the app
      const response = await axios.post('/api/deploy-app', payload);
      
      if (response.data.success) {
        toast.success(`${payload.appType} deployed successfully!`);
        setDeploymentResult(response.data.data);
        
        // Reset form
        setFormData({
          name: '',
          appType: initialAppType || '',
          region: 'us-central1',
          zone: DEFAULT_ZONE,
          machineType: DEFAULT_MACHINE_TYPE,
          apiKeys: {
            gemini_api_key: '',
            anthropic_api_key: '',
            openai_api_key: ''
          },
          customInstructions: '',
          webhookUrls: {
            notifications: '',
            completion: ''
          },
          environment: 'production'
        });
        setApiKeyFields(['gemini_api_key']);
        setWebhookFields([]);
      } else {
        toast.error('Deployment failed. Please try again.');
      }
    } catch (error) {
      console.error('Deployment error:', error);
      toast.error(`Deployment failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  return (
    <div id="deploy-form" className="container mx-auto p-4 max-w-4xl">
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Deploy AI App on GCP</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* App Selection */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select AI Application
              </label>
              <select
                id="appType"
                name="appType"
                value={formData.appType}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select an application</option>
                {aiApps.map(app => (
                  <option key={app.id} value={app.id}>
                    {app.name}
                  </option>
                ))}
              </select>
              {formData.appType && (
                <p className="mt-1 text-sm text-gray-500">
                  {aiApps.find(app => app.id === formData.appType)?.description}
                </p>
              )}
            </div>
            
            {/* Basic Config */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Deployment Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="my-code-reviewer"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Must be unique and contain only lowercase letters, numbers, and hyphens
              </p>
            </div>
            
            {/* Region Selection */}
            <div>
              <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">
                GCP Region
              </label>
              <select
                id="region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {gcpRegions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Machine Type */}
            <div>
              <label htmlFor="machineType" className="block text-sm font-medium text-gray-700 mb-1">
                Machine Type
              </label>
              <select
                id="machineType"
                name="machineType"
                value={formData.machineType}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {machineTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} - {type.price}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Zone Selection */}
            <div>
              <label htmlFor="zone" className="block text-sm font-medium text-gray-700 mb-1">
                Zone
              </label>
              <input
                id="zone"
                name="zone"
                type="text"
                value={formData.zone}
                onChange={handleChange}
                placeholder={DEFAULT_ZONE}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {/* Environment */}
            <div>
              <label htmlFor="environment" className="block text-sm font-medium text-gray-700 mb-1">
                Environment
              </label>
              <select
                id="environment"
                name="environment"
                value={formData.environment}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {environments.map(env => (
                  <option key={env.id} value={env.id}>
                    {env.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* API Keys */}
            <div className="col-span-2">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  API Keys
                </label>
                <button
                  onClick={addApiKeyField}
                  disabled={apiKeyFields.length >= apiKeyOptions.length}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                >
                  + Add API Key
                </button>
              </div>
              
              {apiKeyFields.map((keyField) => {
                const keyOption = apiKeyOptions.find(opt => opt.id === keyField);
                return (
                  <div key={keyField} className="flex space-x-2 mb-3">
                    <div className="w-1/3">
                      <select
                        disabled
                        value={keyField}
                        className="w-full px-3 py-2 border border-gray-200 rounded-md shadow-sm bg-gray-50 text-gray-700"
                      >
                        <option value={keyField}>{keyOption.name}</option>
                      </select>
                    </div>
                    <div className="flex-1 relative">
                      <input
                        type="password"
                        name={`apiKeys.${keyField}`}
                        value={formData.apiKeys[keyField] || ''}
                        onChange={handleChange}
                        placeholder="Enter API key"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      {apiKeyFields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeApiKeyField(keyField)}
                          className="absolute right-2 top-2 text-gray-400 hover:text-red-500"
                          aria-label="Remove API key field"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Custom Instructions */}
            <div className="col-span-2">
              <label htmlFor="customInstructions" className="block text-sm font-medium text-gray-700 mb-1">
                Custom Instructions
              </label>
              <textarea
                id="customInstructions"
                name="customInstructions"
                value={formData.customInstructions}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add any custom instructions for the AI app..."
              />
            </div>
            
            {/* Webhook URLs */}
            <div className="col-span-2">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Webhook URLs (Optional)
                </label>
                <button
                  onClick={addWebhookField}
                  disabled={webhookFields.length >= webhookOptions.length}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                >
                  + Add Webhook
                </button>
              </div>
              
              {webhookFields.map((webhookField) => {
                const webhookOption = webhookOptions.find(opt => opt.id === webhookField);
                return (
                  <div key={webhookField} className="flex space-x-2 mb-3">
                    <div className="w-1/3">
                      <select
                        disabled
                        value={webhookField}
                        className="w-full px-3 py-2 border border-gray-200 rounded-md shadow-sm bg-gray-50 text-gray-700"
                      >
                        <option value={webhookField}>{webhookOption.name}</option>
                      </select>
                    </div>
                    <div className="flex-1 relative">
                      <input
                        type="url"
                        name={`webhookUrls.${webhookField}`}
                        value={formData.webhookUrls[webhookField] || ''}
                        onChange={handleChange}
                        placeholder="https://example.com/webhook"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeWebhookField(webhookField)}
                        className="absolute right-2 top-2 text-gray-400 hover:text-red-500"
                        aria-label="Remove webhook field"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Submit Button */}
          <div className="mt-8">
            <button
              type="submit"
              disabled={isDeploying || !formData.name}
              className={`
                w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
                ${isDeploying || !formData.name 
                  ? 'bg-blue-300 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}
              `}
            >
              {isDeploying ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deploying...
                </>
              ) : (
                'Deploy App'
              )}
            </button>
          </div>
        </form>
        
        {/* Deployment Result */}
        {deploymentResult && (
          <div className="mt-8 p-4 border border-green-200 rounded-lg bg-green-50">
            <h3 className="font-bold text-green-800 mb-2">Deployment Successful!</h3>
            <div className="text-sm text-gray-700">
              <p><span className="font-medium">App Name:</span> {deploymentResult.name}</p>
              <p><span className="font-medium">App Type:</span> {deploymentResult.appType}</p>
              <p><span className="font-medium">API URL:</span> {deploymentResult.apiUrl}</p>
              <p><span className="font-medium">Status:</span> {deploymentResult.status}</p>
              <p><span className="font-medium">Deployed at:</span> {new Date(deploymentResult.createdAt).toLocaleString()}</p>
            </div>
            <div className="mt-4">
              <div className="bg-gray-100 p-3 rounded-md">
                <p className="font-mono text-sm">
                  API Endpoint: <span className="text-blue-700">{deploymentResult.apiUrl}/review-code</span>
                </p>
                <p className="font-mono text-sm mt-2">
                  Health Check: <span className="text-blue-700">{deploymentResult.apiUrl.replace('/api', '')}/health</span>
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                It may take a few minutes for the VM to fully initialize and the API to become available.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeploymentForm; 